<template lang="pug">
.service-page
  .content
    .intro-page
      // 头部标题区
      .intro-header
        .header-logo
          img.logo(src="@/assets/logo.png")
          span.logo-text 袋鼠照护
        .header-title 早产儿家庭照护服务包
        .header-subtitle 医学证据支持 · 专业团队跟进 · 个性化照护计划
        .header-pre-slogan 早产宝宝的发育风险，往往不是现在出现
        .header-slogan 把握最关键的前 6 个月，走稳、走对

      // 第一部分:为什么需要专业照护
      .section.section-why
        .section-title
          .title-number 一
          span.title-text 为什么早产宝宝“现在看着好”，也值得专业照护？
          span.title-tag 必须看
        .section-content
          .research-note
            span.research-source 国际顶级医学期刊JAMA《美国医学会杂志》 Network Open 2024 论文（Neurodevelopment at Age 9 Years Among Children Born at 32 to 36 Weeks’ Gestation）对 32–36 周早产儿进行了 9 年随访，结果非常明确：
          
          .highlight-points
            .point-item
              .point-icon ✦
              .point-content
                span.point-title 发育不会自然追上
                span.point-desc 在 语言、数学、学习能力、注意力、行为控制、精细运动等多个领域，中晚期早产儿在 9 岁时仍显著落后足月儿，这些差距，往往在婴儿期并不明显。
            
            .point-item
              .point-icon ✦
              .point-content
                span.point-title 2 岁前的轻微落后，会影响 8、9 岁后的学业与行为问题
                span.point-desc 婴儿期看似"稍慢一点"，但如果缺乏科学及时干预，会在学龄阶段被放大。
            
            .point-item
              .point-icon ✦
              .point-content
                span.point-title 既然 32–36 周都有风险,那 <32 周的宝宝更需要专业照护
                span.point-desc 医学研究长期证实：胎龄越小，大脑越不成熟，风险越高。32 周以下(包括极早、超早)更是典型的需要长期随访与家庭干预的人群。

      // 第二部分:为什么家长很难做到
      .section.section-challenge
        .section-title
          .title-number 二
          span.title-text 为什么家长自己很难做到科学照护?
        .section-content
          .challenge-list
            .challenge-item
              .challenge-number ①
              .challenge-content
                span.challenge-title 家长不知道"什么时候该做什么做到什么程度"
                span.challenge-desc 喂养、神经发育训练、睡眠、环境布置、体重增速、反流、胀气……每个环节都与足月儿不同，每个早产儿也都不一样。靠自己查资料，容易用错方法。
            
            .challenge-item
              .challenge-number ②
              .challenge-content
                span.challenge-title 很多发育偏差外表完全看不出来
                span.challenge-desc 注意力、执行功能、精细动作、语言发展差异往往要到 18 个月–9 岁才显现。婴儿期靠家长肉眼很难识别。
            
            .challenge-item
              .challenge-number ③
              .challenge-content
                span.challenge-title 靠"几个月一次的随访"远远不够
                span.challenge-desc 发育问题是积累性的，需要每周甚至每日监测与调整，而不是"等下次门诊再看"。
            
            .challenge-item
              .challenge-number ④
              .challenge-content
                span.challenge-title 家长描述 ≠ 宝宝真实状态
                span.challenge-desc 上门数据反复证明：家长对"吃奶量、动作质量、训练合适度、睡眠环境"等的判断常常与实际不一致，而这些差异会影响孩子的追赶速度。
          
          .conclusion-box
            span.conclusion-text 早产儿照护是一个"专业决策过程"，不是靠爱和努力就能做好的事。

      // 第三部分:我们能为你做什么
      .section.section-service
        .section-title
          .title-number 三
          span.title-text 袋鼠照护服务包能为你解决什么问题?
        .section-content
          .service-cards
            // 服务卡片1
            .service-card
              .card-header
                span.card-number 1)
                span.card-title 5 次专业上门访视：避免家长“以为没问题，其实已经偏了”
              .card-body
                span.card-intro 我们到你家里进行医学级评估:
                .card-list
                  span.list-item • 奶量、吞咽、喂养姿势
                  span.list-item • 睡眠安全、环境优化
                  span.list-item • 张力、姿势、粗大/精细运动能力
                  span.list-item • 脑功能行为反应
                  span.list-item • 标准化体重/头围测量
                  span.list-item • 手把手训练方法纠正
                span.card-highlight 线上看不出的，上门一眼能看出。是整个照护体系中价值最高的一环。

            // 服务卡片2
            .service-card
              .card-header
                span.card-number 2)
                span.card-title 每周电话回访：主动发现问题，而不是等小问题积累成大问题，早产儿的问题，发现早 = 调整成本低
              .card-body
                span.card-intro 我们会每周询问：
                .card-list
                  span.list-item • 奶量是否需要调整?
                  span.list-item • 睡眠是否规律?
                  span.list-item • 大便是否异常?
                  span.list-item • 当前训练是否适合?
                  span.list-item • 有无反流、胀气、过度兴奋?
                  span.list-item • 家长焦虑程度是否增加?
                span.card-highlight 所有需要纠正的地方，我们会主动发现，不让小问题累积成大问题。

            // 服务卡片3
            .service-card
              .card-header
                span.card-number 3)
                span.card-title 24 小时线上答疑：时间敏感的问题不过夜，早产儿的紧急情况，不能等
              .card-body
                span.card-intro 早产儿的问题，不等天亮。我们提供：
                .card-list
                  span.list-item • 夜间紧急情况快速响应
                  span.list-item • 训练/喂养即时指导
                  span.list-item • 反流、异常哭闹、吃奶突降的处理方案
                  span.list-item • 专家支持升级入口
                span.card-highlight 付费用户享有优先通道，不会再"等照护师什么时候有空"。

            // 服务卡片4
            .service-card
              .card-header
                span.card-number 4)
                span.card-title AI + 专家团队：为宝宝生成"个性化照护路径"，避免家长"盲目试错"
              .card-body
                span.card-intro 我们的 AI 系统整合：
                .card-list
                  span.list-item • 医嘱
                  span.list-item • 上门评估数据
                  span.list-item • 每周回访
                  span.list-item • 宝宝每日体重/吃奶/睡眠数据
                  span.list-item • 家长上传视频
                  span.list-item • 随访复查报告
                span.card-summary 自动分析宝宝的发育状态，由照护团队审核后生成：
                span.card-summary 宝宝下一阶段要做的训练、喂养计划、安全注意事项、监测指标。
                span.card-highlight 家长只需按计划执行 —— 不用自己查、不用猜、不犯错，让宝宝的成长更有确定性，也让家庭不被长期的不确定性消耗。

      // 第四部分:科学依据
      .section.section-evidence
        .section-title
          .title-number 四
          span.title-text 来源于顶刊的科学依据
        .section-content
          .evidence-list
            .evidence-item(@click="openLink('https://jamanetwork.com/journals/jamanetworkopen/fullarticle/2826283')")
              span.evidence-text • JAMA Network Open 2024（《美国医学会杂志》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://publications.aap.org/pediatrics/article/152/6/e2023061878/195044/Risk-of-Developmental-Disorders-in-Children-Born')")
              span.evidence-text • JAMA Pediatrics（美国儿科学会《儿科学》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://pmc.ncbi.nlm.nih.gov/articles/PMC7082240/')")
              span.evidence-text • Translational Pediatrics （《转化儿科学杂志》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://pubmed.ncbi.nlm.nih.gov/38267070/')")
              span.evidence-text • The BMJ （《英国医学杂志》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://pubmed.ncbi.nlm.nih.gov/27479919/')")
              span.evidence-text • JAMA Pediatrics（美国儿科学会《儿科学》）
              span.evidence-icon →

      // 第五部分:最终获得
      .section.section-result
        .section-title
          .title-number 五
          span.title-text 最终获得
        .section-content
          .result-list
            .result-item
              span.result-check ✔
              span.result-text 宝宝更快追赶体格与能力
            .result-item
              span.result-check ✔
              span.result-text 早期发现问题，避免错过干预窗口
            .result-item
              span.result-check ✔
              span.result-text 家长省心，不再到处查资料，简单照做即可
            .result-item
              span.result-check ✔
              span.result-text 减少不必要就医与家庭焦虑
            .result-item
              span.result-check ✔
              span.result-text 更稳定的家庭节奏与育儿信心
          
          .final-message
            span.final-text 这不是简单的上门和线上答疑，而是给孩子一套"正确成长的系统"。

      // 风险 vs 保障对比表
      .section.section-comparison
        .section-title
          span.title-text 风险 vs 保障对比表
        .section-content
          .comparison-table
            .table-header
              span.header-item.header-item-main 情况
              span.header-item 不升级
              span.header-item.header-item-highlight 升级服务
            .table-row
              span.row-label 发育节奏是否有人盯
              span.row-value ❌
              span.row-value.row-value-highlight ✔
            .table-row
              span.row-label 偏差是否早发现
              span.row-value ❌
              span.row-value.row-value-highlight ✔
            .table-row
              span.row-label 是否动态调整计划
              span.row-value ❌
              span.row-value.row-value-highlight ✔
            .table-row
              span.row-label 是否反复试错
              span.row-value 可能性高
              span.row-value.row-value-highlight 低
            .table-row
              span.row-label 风险是否更可控
              span.row-value ❌
              span.row-value.row-value-highlight ✔
          
          .comparison-note
            span.note-text 这是一次用相对低的成本，换取宝宝关键阶段"更高确定性"的选择。

      // 服务包价格标题
      .package-compare-title
        span.pc-title 服务包价格(最长服务5年)

      // 支付方式选择器
      .payment-mode-selector
        .mode-option(:class="{active: paymentMode === 'onetime'}" @click="paymentMode = 'onetime'")
          .option-icon
            span.icon-check(v-if="paymentMode === 'onetime'") &#10003;
          .option-content
            span.option-title 按半年订阅
            span.option-subtitle 半年付一次，享受折扣优惠
        .mode-option(:class="{active: paymentMode === 'subscription'}" @click="paymentMode = 'subscription'")
          .option-icon
            span.icon-check(v-if="paymentMode === 'subscription'") &#10003;
          .option-content
            span.option-title 按月订阅
            span.option-subtitle 自动续费，随时可取消
        .mode-option(:class="{active: paymentMode === 'yearly'}" @click="paymentMode = 'yearly'")
          .option-icon
            span.icon-check(v-if="paymentMode === 'yearly'") &#10003;
          .option-content
            span.option-title 按年订阅
            span.option-subtitle 一年付一次，最高优惠
            span.option-badge 推荐

      // 基础版 / 尊享版（按半年订阅）对比区块
      .package-compare(v-if="paymentMode === 'onetime'")
        .compare-inner
          .compare-header
            span.compare-title 服务项
            span.compare-col 基础版
            span.compare-col.vip 按半年订阅

          .compare-row
            span.row-key 服务周期
            span.row-val 24个月基础
            .row-val
              div 180天个性化
              div +18个月基础

          .compare-row
            span.row-key 上门访视
            span.row-val 送首次
            span.row-val 送首次 + 5次

          .compare-row
            span.row-key 图文咨询
            span.row-val 工作日8-17点
            span.row-val 7*24 小时

          .compare-row
            span.row-key 电话咨询
            span.row-val —
            span.row-val 6 个月

          .compare-row
            span.row-key 喂养调整
            span.row-val 问题解答
            span.row-val 调整喂养

          .compare-row
            .row-key
              div 神经发育
              div 家庭促进
            span.row-val —
            span.row-val 6个月

          .compare-row
            span.row-key 门诊陪诊
            span.row-val 1次
            span.row-val 包含

          .compare-row
            span.row-key 电话回访
            span.row-val 2次
            span.row-val 每周1次

          .compare-row
            span.row-key 随访提醒
            span.row-val —
            span.row-val 包含

          .compare-row
            span.row-key 辅食指导
            span.row-val —
            span.row-val 包含

          .compare-row
            span.row-key 隔代养育冲突
            span.row-val —
            span.row-val 包含

          .compare-row
            span.row-key 婴儿体重秤
            span.row-val —
            span.row-val 包含

          .compare-row.compare-price
            span.row-key 价格
            span.row-val 免费
            .row-val
              div 原价7200
              div 折后5280元

      // 按月订阅方案展示
      .subscription-plan(v-if="paymentMode === 'subscription'")
        .plan-header
          span.plan-title 按月订阅服务包
          span.plan-badge 自动续费 · 随时取消
        
        .plan-content
          .plan-price-section
            .price-main
              span.price-currency ¥
              span.price-amount 1099
              span.price-unit /月
            .price-desc 首月立享，次月自动续费
          
          .plan-features
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 享受按半年订阅的全部服务
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 灵活订阅，无需一次性支付大额费用
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 随时取消，未使用部分按比例退款
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 自动续费，服务不中断
          
          .plan-note
            span.note-title 💡 订阅说明：
            .note-list
              span.note-item • 订阅周期：30天/月
              span.note-item • 自动续费：到期前3天自动扣款，可随时取消
              span.note-item • 退订规则：可随时在个人中心取消订阅，次月不再扣费
              span.note-item • 退款政策：与按半年订阅相同的按使用量退款规则
          
          .plan-compare
            .compare-item
              span.compare-label 按半年订阅
              span.compare-value 5280元
              span.compare-tip 半年订一次，更优惠
            .compare-divider vs
            .compare-item.highlight
              span.compare-label 按月订阅
              span.compare-value 1099元×6月=6594元
              span.compare-tip 分摊压力，灵活可控

      // 按年订阅方案展示
      .subscription-plan(v-if="paymentMode === 'yearly'")
        .plan-header
          span.plan-title 按年订阅服务包
          span.plan-badge 全年服务 · 最高优惠
        
        .plan-content
          .plan-price-section
            .price-main
              span.price-currency ¥
              span.price-amount 9504
              span.price-unit /年
            .price-desc 一次性支付全年费用，平均792元/月
          
          .plan-features
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 享受全年完整服务，持续专业照护
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 相比按月订阅节省3684元（年省28%）
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 相比按半年订阅节省1056元（年省10%）
            .feature-item
              span.feature-icon &#10003;
              span.feature-text 一次付费，全年无忧，服务不中断
          
          .plan-note
            span.note-title 💡 订阅说明：
            .note-list
              span.note-item • 订阅周期：365天/年
              span.note-item • 服务内容：包含全年的上门访视、电话回访、24小时在线答疑等全部服务
              span.note-item • 退订规则：可随时申请退订，未使用部分按比例退款
              span.note-item • 退款政策：与其他订阅方式相同的按使用量退款规则
          
          .plan-compare
            .compare-item
              span.compare-label 按月订阅
              span.compare-value 1099元×12月=13188元
              span.compare-tip 每月付费
            .compare-divider vs
            .compare-item
              span.compare-label 按半年订阅
              span.compare-value 5280元×2=10560元
              span.compare-tip 半年付一次
            .compare-divider vs
            .compare-item.highlight
              span.compare-label 按年订阅
              span.compare-value 9504元/年
              span.compare-tip 最高优惠，省3684元

      // 第六部分:退款说明
      .section.section-refund
        .section-title
          .title-number 六
          span.title-text 退款说明
        .section-content
          .refund-principle
            span.principle-title 随时退款原则
            span.principle-desc 袋鼠照护服务包支持随时退款，退款金额按实际使用情况计算，确保您的权益。

          .refund-details
            span.details-title 服务计费说明：
            
            .refund-item
              .item-header
                span.item-number 1
                span.item-title 线上服务
              span.item-desc 按天计算，每次价格:29.9元/天(折后价格:21.8元)

            .refund-item
              .item-header
                span.item-number 2
                span.item-title 上门服务
              span.item-desc 按次数消耗计算，599元/次(折后价格:437元)。首次上门为赠送，不计入费用。

            .refund-item
              .item-header
                span.item-number 3
                span.item-title 实物(奶粉、全新体重秤)
              span.item-desc 实物根据用户指定时间寄送。若退款时实物未寄送，不产生费用；若已寄送并使用，将扣除相应费用：
              .item-sub
                span.sub-item • 奶粉：235元/罐(折后价格：171.5元)
                span.sub-item • 全新体重秤：150元/台(折后价格：109.5元)

            .refund-item
              .item-header
                span.item-number 4
                span.item-title 其他服务
              span.item-desc 主要在上门或线上服务中完成，费用已计算在第1和第2项中。

          .refund-example
            span.example-title 💡 退款计算示例：
            span.example-desc 订阅5280元按半年付费服务，使用了30天，完成3次上门服务，奶粉和体重秤均已使用：
            .example-calc
              span.calc-line 5280元(原价)
              span.calc-line - 437元(首次上门赠送后的费用)
              span.calc-line - 109.5元(全新体重秤)
              span.calc-line - 654元(30天线上 × 21.8元)
              span.calc-line - 1311元(3次上门 × 437元)
              span.calc-result = 2768.5元(退款金额)

  // 底部固定购买栏
  .bottom-bar(v-if="showPurchaseBar")
    .bar-content
      .price-info
        .price-row(v-if="paymentMode === 'onetime'")
          span.price-label 按半年订阅
          .price-value
            span.original-price ¥7200
            span.current-price ¥5280
        .price-row(v-else-if="paymentMode === 'subscription'")
          span.price-label 按月订阅
          .price-value
            span.current-price ¥1099
            span.price-unit /月
        .price-row(v-else-if="paymentMode === 'yearly'")
          span.price-label 按年订阅
          .price-value
            span.original-price ¥13188
            span.current-price ¥9504
      .action-buttons
        button.btn-purchase(@click="handlePurchase")
          span(v-if="paymentMode === 'onetime'") 立即订阅（半年付）
          span(v-else-if="paymentMode === 'subscription'") 立即订阅（按月）
          span(v-else-if="paymentMode === 'yearly'") 立即订阅（按年）
</template>
<script>
export default {
    data () {
        return {
            regions: [],
            paymentMode: 'subscription', // 'onetime' 或 'subscription'
            showPurchaseBar: true
        };
    },
    mounted () {
        this.fetchPurchasedInfo();
    },
    methods: {
    // 拉取购买信息
        fetchPurchasedInfo () {
            // 这里可以根据需要添加 API 调用
            // 示例数据
            this.regions = [
                { city: '北京', name: '张**' },
                { city: '上海', name: '王**' },
                { city: '深圳', name: '李**' }
            ];
        },
        // 领取优惠券
        goToCoupon () {
            this.$router.push('/pay2/coupon');
        },
        // 打开外部链接
        openLink (url) {
            window.open(url, '_blank');
        },
        // 处理购买
        handlePurchase () {
            if (this.paymentMode === 'onetime') {
                // 按半年订阅（半年付一次）
                this.$toast('跳转到按半年订阅支付页面');
                // this.$router.push({ path: '/pay2/checkout', query: { type: 'onetime', amount: 5280 } });
            } else if (this.paymentMode === 'subscription') {
                // 按月订阅
                this.$toast('跳转到按月订阅支付页面');
                // this.$router.push({ path: '/pay2/checkout', query: { type: 'subscription', amount: 1099 } });
            } else if (this.paymentMode === 'yearly') {
                // 按年订阅
                this.$toast('跳转到按年订阅支付页面');
                // this.$router.push({ path: '/pay2/checkout', query: { type: 'yearly', amount: 9504 } });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
/* 新版介绍页样式 */
.service-page {
  background: #f8f8f8;
  position: relative;
  min-height: 100vh;

  .content {
    padding-bottom: 1.6rem; // 为底部固定栏留出空间
  }

  .intro-page {
    background: #fff;
    
    // 头部标题区
    .intro-header {
      background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
      padding: 32px 18px;
      text-align: center;
      color: #fff;
      
      .header-logo {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        
        .logo {
          width: 32px;
          height: 32px;
          margin-right: 8px;
        }
        
        .logo-text {
          font-size: 16px;
          font-weight: 600;
        }
      }
      
      .header-title {
        font-size: 22px;
        font-weight: 700;
        margin-bottom: 12px;
        line-height: 1.4;
      }
      
      .header-subtitle {
        font-size: 16px;
        font-weight: 500;
        opacity: 0.95;
        margin-bottom: 8px;
      }
      
      .header-pre-slogan {
        font-size: 14px;
        font-weight: 400;
        opacity: 0.9;
        margin-bottom: 12px;
        margin-top: 16px;
      }
      
      .header-slogan {
        font-size: 18px;
        font-weight: 600;
        background: rgba(255, 255, 255, 0.2);
        padding: 8px 16px;
        border-radius: 20px;
        display: inline-block;
        margin-top: 8px;
      }
    }
    
    // 通用区块样式
    .section {
      padding: 24px 18px;
      border-bottom: 8px solid #f8f8f8;
      
      .section-title {
        display: flex;
        align-items: center;
        margin-bottom: 18px;
        
        .title-number {
          width: 32px;
          height: 32px;
          background: #05C2CB;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          margin-right: 12px;
          flex-shrink: 0;
        }
        
        .title-text {
          font-size: 18px;
          font-weight: 700;
          color: #000;
          flex: 1;
        }
        
        .title-tag {
          background: #FF6B6B;
          color: #fff;
          font-size: 12px;
          padding: 4px 12px;
          border-radius: 12px;
          margin-left: 8px;
        }
      }
      
      .section-content {
        font-size: 14px;
        line-height: 1.8;
        color: #333;
      }
    }
    
    // 第一部分样式
    .section-why {
      .research-note {
        background: #FFF9E6;
        padding: 16px;
        border-radius: 8px;
        margin-bottom: 18px;
        border-left: 4px solid #FFC107;
        
        .research-source {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }
      
      .highlight-points {
        .point-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 18px;
          padding: 16px;
          background: #F6F7F9;
          border-radius: 8px;
          
          .point-icon {
            color: #05C2CB;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
            margin-top: 2px;
          }
          
          .point-content {
            flex: 1;
            
            .point-title {
              display: block;
              font-size: 15px;
              font-weight: 600;
              color: #000;
              margin-bottom: 8px;
            }
            
            .point-desc {
              display: block;
              font-size: 14px;
              color: #666;
              line-height: 1.6;
            }
          }
        }
      }
    }
    
    // 第二部分样式
    .section-challenge {
      .challenge-list {
        .challenge-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 18px;
          
          .challenge-number {
            width: 28px;
            height: 28px;
            background: #E8F7F8;
            color: #05C2CB;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 600;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .challenge-content {
            flex: 1;
            
            .challenge-title {
              display: block;
              font-size: 15px;
              font-weight: 600;
              color: #000;
              margin-bottom: 6px;
            }
            
            .challenge-desc {
              display: block;
              font-size: 14px;
              color: #666;
              line-height: 1.6;
            }
          }
        }
      }
      
      .conclusion-box {
        background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
        padding: 18px;
        border-radius: 8px;
        margin-top: 18px;
        text-align: center;
        
        .conclusion-text {
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.6;
        }
      }
    }
    
    // 第三部分样式
    .section-service {
      .service-cards {
        .service-card {
          background: #F6F7F9;
          border-radius: 12px;
          padding: 18px;
          margin-bottom: 16px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .card-header {
            display: flex;
            align-items: flex-start;
            margin-bottom: 12px;
            
            .card-number {
              color: #05C2CB;
              font-size: 16px;
              font-weight: 700;
              margin-right: 8px;
              flex-shrink: 0;
            }
            
            .card-title {
              font-size: 16px;
              font-weight: 600;
              color: #000;
              line-height: 1.4;
              flex: 1;
            }
          }
          
          .card-body {
            padding-left: 24px;
            
            .card-intro {
              display: block;
              font-size: 14px;
              color: #666;
              margin-bottom: 12px;
            }
            
            .card-list {
              .list-item {
                display: block;
                font-size: 14px;
                color: #666;
                line-height: 1.8;
                padding-left: 8px;
              }
            }
            
            .card-summary {
              display: block;
              font-size: 14px;
              color: #666;
              line-height: 1.6;
              margin-top: 12px;
            }
            
            .card-highlight {
              display: block;
              background: #fff;
              padding: 12px;
              border-radius: 6px;
              margin-top: 12px;
              font-size: 14px;
              color: #05C2CB;
              font-weight: 500;
              line-height: 1.6;
              border-left: 3px solid #05C2CB;
            }
          }
        }
      }
    }
    
    // 第四部分样式
    .section-evidence {
      .evidence-list {
        .evidence-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px;
          background: #F6F7F9;
          border-radius: 8px;
          margin-bottom: 10px;
          cursor: pointer;
          
          &:active {
            background: #E8F7F8;
          }
          
          .evidence-text {
            font-size: 14px;
            color: #000;
            flex: 1;
          }
          
          .evidence-icon {
            color: #05C2CB;
            font-size: 18px;
            margin-left: 12px;
          }
        }
      }
    }
    
    // 第五部分样式
    .section-result {
      .result-list {
        .result-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 14px;
          padding: 14px;
          background: #E8F7F8;
          border-radius: 8px;
          
          .result-check {
            color: #05C2CB;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .result-text {
            font-size: 15px;
            color: #000;
            line-height: 1.6;
            flex: 1;
          }
        }
      }
      
      .final-message {
        background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
        padding: 20px;
        border-radius: 12px;
        margin-top: 20px;
        text-align: center;
        
        .final-text {
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          line-height: 1.6;
        }
      }
    }

    // 风险 vs 保障对比表样式
    .section-comparison {
      .comparison-table {
        background: #fff;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #E6EEF0;
        
        .table-header {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr;
          background: linear-gradient(90deg, #F6FBFB 0%, #FFFFFF 100%);
          border-bottom: 2px solid #05C2CB;
          
          .header-item {
            padding: 14px 12px;
            font-size: 14px;
            font-weight: 700;
            color: #333;
            text-align: center;
            
            &.header-item-main {
              text-align: left;
            }
            
            &.header-item-highlight {
              color: #05C2CB;
            }
          }
        }
        
        .table-row {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr;
          border-bottom: 1px solid #EEF6F6;
          
          &:last-child {
            border-bottom: none;
          }
          
          .row-label {
            padding: 14px 12px;
            font-size: 14px;
            color: #333;
            font-weight: 500;
          }
          
          .row-value {
            padding: 14px 12px;
            font-size: 16px;
            text-align: center;
            color: #000;
            
            &.row-value-highlight {
              color: #05C2CB;
              font-weight: 600;
            }
          }
        }
      }
      
      .comparison-note {
        background: #FFF9E6;
        padding: 14px 16px;
        border-radius: 8px;
        margin-top: 16px;
        text-align: center;
        border-left: 3px solid #FFC107;
        
        .note-text {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }
    }

    // 优惠券入口
    .coupon-entry {
      margin: 16px 16px 0;
      padding: 16px;
      background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 0 4px 12px rgba(255, 107, 107, 0.2);
      position: relative;
      overflow: hidden;
      cursor: pointer;

      &::before {
        content: '';
        position: absolute;
        right: -20px;
        top: -20px;
        width: 100px;
        height: 100px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
      }

      .coupon-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        z-index: 1;

        .coupon-icon {
          width: 44px;
          height: 44px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);

          .icon-tag {
            font-size: 20px;
            font-weight: bold;
            color: #fff;
          }
        }

        .coupon-info {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .coupon-title {
            font-size: 16px;
            font-weight: 600;
            color: #fff;
            line-height: 1.2;
          }

          .coupon-desc {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.2;
          }
        }
      }

      .coupon-right {
        display: flex;
        align-items: center;
        gap: 4px;
        z-index: 1;

        .coupon-action {
          font-size: 14px;
          font-weight: 500;
          color: #fff;
        }

        .arrow {
          font-size: 24px;
          color: #fff;
          font-weight: 300;
        }
      }

      &:active {
        opacity: 0.9;
        transform: scale(0.98);
      }
    }

    /* 服务包价格标题 */
    .package-compare-title {
      margin: 12px 16px 6px;
      padding-left: 6px;
      
      .pc-title {
        font-size: 16px;
        font-weight: 700;
        color: #333;
      }
    }

    /* 支付方式选择器 */
    .payment-mode-selector {
      margin: .32rem .36rem;
      display: flex;
      gap: .24rem;

      .mode-option {
        flex: 1;
        background: #F6F7F9;
        border: .04rem solid transparent;
        border-radius: .24rem;
        padding: .28rem;
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;

        &.active {
          background: linear-gradient(135deg, #E8F7F8 0%, #F0FAFB 100%);
          border-color: #05C2CB;
        }

        .option-icon {
          width: .4rem;
          height: .4rem;
          border: .04rem solid #DADADA;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          top: .24rem;
          right: .24rem;
          background: #fff;

          .icon-check {
            color: #05C2CB;
            font-size: .28rem;
            font-weight: 700;
          }
        }

        &.active .option-icon {
          border-color: #05C2CB;
          background: #05C2CB;

          .icon-check {
            color: #fff;
          }
        }

        .option-content {
          display: flex;
          flex-direction: column;
          padding-right: .56rem;

          .option-title {
            font-size: .32rem;
            font-weight: 700;
            color: #000;
            margin-bottom: .12rem;
          }

          .option-subtitle {
            font-size: .26rem;
            color: #666;
            line-height: 1.4;
          }

          .option-badge {
            display: inline-block;
            background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
            color: #fff;
            font-size: .22rem;
            padding: .06rem .16rem;
            border-radius: .2rem;
            margin-top: .12rem;
            align-self: flex-start;
            font-weight: 600;
          }
        }
      }
    }

    /* 基础版 / 尊享半年包对比样式（使用 grid 对齐列） */
    .package-compare {
      margin: 12px 16px 8px;
      border-radius: 8px;
      background: #fff;
      border: 1px solid #E6EEF0;
      /* scroll-view 需要设置宽度，通常是 auto 或 100% */
      width: auto;
      overflow-x: auto; /* 允许横向滚动 */

      .compare-inner {
        min-width: 360px; /* 稍微加大一点宽度确保显示宽松 */
        padding-bottom: 2px;
      }

      .compare-header,
      .compare-row {
        display: grid;
        /* 固定三列比例，配合 min-width: 520px 实现横向滚动 */
        grid-template-columns: 90px 1fr 1.2fr;
        align-items: center;
        padding: 12px 10px;
        border-bottom: 1px solid #EEF6F6;
        column-gap: 10px;
      }

      .compare-header {
        background: linear-gradient(90deg, #F6FBFB 0%, #FFFFFF 100%);
        font-weight: 700;
      }

      .compare-title {
        color: #666;
        font-size: 13px;
      }

      .compare-col {
        text-align: left;
        color: #333;
        font-size: 13px;
      }

      .compare-col.vip {
        color: #07C160;
      }

      .row-key {
        color: #666;
        font-size: 13px;
        white-space: normal;
        line-height: 1.4;
      }

      .row-val {
        text-align: left;
        color: #333;
        font-size: 13px;
        white-space: normal;
        line-height: 1.4;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .compare-price .row-val {
        font-weight: 700;
        color: #05C2CB;
      }
    }

    // 第六部分:退款说明样式
    .section-refund {
      .refund-principle {
        background: #FFF9E6;
        padding: 16px;
        border-radius: 8px;
        margin-bottom: 18px;
        border-left: 4px solid #FFC107;

        .principle-title {
          display: block;
          font-size: 16px;
          font-weight: 700;
          color: #000;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .principle-desc {
          display: block;
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }

      .refund-details {
        .details-title {
          display: block;
          font-size: 15px;
          font-weight: 600;
          color: #000;
          margin-bottom: 16px;
        }

        .refund-item {
          background: #F6F7F9;
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 12px;

          .item-header {
            display: flex;
            align-items: center;
            margin-bottom: 8px;

            .item-number {
              width: 24px;
              height: 24px;
              background: #05C2CB;
              color: #fff;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 13px;
              font-weight: 600;
              margin-right: 10px;
              flex-shrink: 0;
            }

            .item-title {
              font-size: 15px;
              font-weight: 600;
              color: #000;
            }
          }

          .item-desc {
            display: block;
            font-size: 14px;
            color: #666;
            line-height: 1.6;
            padding-left: 34px;
          }

          .item-sub {
            padding-left: 34px;
            margin-top: 8px;

            .sub-item {
              display: block;
              font-size: 13px;
              color: #666;
              line-height: 1.8;
            }
          }
        }
      }

      .refund-example {
        background: linear-gradient(135deg, #E8F7F8 0%, #F0FAFB 100%);
        border-radius: 12px;
        padding: 16px;
        margin-top: 18px;

        .example-title {
          display: block;
          font-size: 15px;
          font-weight: 700;
          color: #05C2CB;
          margin-bottom: 12px;
        }

        .example-desc {
          display: block;
          font-size: 14px;
          color: #333;
          line-height: 1.6;
          margin-bottom: 14px;
        }

        .example-calc {
          background: #fff;
          border-radius: 8px;
          padding: 14px;
          border-left: 3px solid #05C2CB;

          .calc-line {
            display: block;
            font-size: 13px;
            color: #666;
            line-height: 1.8;
            font-family: 'Monaco', 'Courier New', monospace;
          }

          .calc-result {
            display: block;
            font-size: 15px;
            color: #05C2CB;
            font-weight: 700;
            line-height: 1.8;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed #E6E6E6;
            font-family: 'Monaco', 'Courier New', monospace;
          }
        }
      }
    }
  }

  /* 按月订阅方案展示 */
  .subscription-plan {
    margin: .32rem .36rem;
    background: linear-gradient(135deg, #FFFFFF 0%, #F8FEFF 100%);
    border: .04rem solid #05C2CB;
    border-radius: .24rem;
    padding: .4rem;
    position: relative;

    .plan-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: .4rem;
      padding-bottom: .32rem;
      border-bottom: .02rem solid #E6F7F8;

      .plan-title {
        font-size: .36rem;
        font-weight: 700;
        color: #000;
      }

      .plan-badge {
        background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
        color: #fff;
        font-size: .24rem;
        padding: .1rem .24rem;
        border-radius: .24rem;
        font-weight: 600;
      }
    }

    .plan-content {
      .plan-price-section {
        text-align: center;
        background: #FFFFFF;
        border-radius: .24rem;
        padding: .4rem;
        margin-bottom: .4rem;
        box-shadow: 0 .04rem .24rem rgba(5, 194, 203, 0.1);

        .price-main {
          display: flex;
          align-items: baseline;
          justify-content: center;
          margin-bottom: .16rem;

          .price-currency {
            font-size: .4rem;
            color: #05C2CB;
            font-weight: 600;
            margin-right: .08rem;
          }

          .price-amount {
            font-size: .8rem;
            color: #05C2CB;
            font-weight: 700;
            line-height: 1;
          }

          .price-unit {
            font-size: .32rem;
            color: #666;
            margin-left: .08rem;
          }
        }

        .price-desc {
          font-size: .26rem;
          color: #999;
        }
      }

      .plan-features {
        margin-bottom: .4rem;

        .feature-item {
          display: flex;
          align-items: flex-start;
          padding: .2rem 0;

          .feature-icon {
            width: .4rem;
            height: .4rem;
            background: #05C2CB;
            color: #fff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: .28rem;
            font-weight: 700;
            margin-right: .2rem;
            flex-shrink: 0;
          }

          .feature-text {
            font-size: .3rem;
            color: #333;
            line-height: 1.6;
          }
        }
      }

      .plan-note {
        background: #FFF9E6;
        border-radius: .16rem;
        padding: .28rem;
        border-left: .06rem solid #FFC107;
        margin-bottom: .4rem;

        .note-title {
          display: block;
          font-size: .28rem;
          font-weight: 600;
          color: #000;
          margin-bottom: .2rem;
        }

        .note-list {
          display: flex;
          flex-direction: column;
          gap: .12rem;

          .note-item {
            font-size: .26rem;
            color: #666;
            line-height: 1.6;
          }
        }
      }

      .plan-compare {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: .24rem;

        .compare-item {
          flex: 1;
          background: #F6F7F9;
          border-radius: .16rem;
          padding: .24rem;
          text-align: center;

          &.highlight {
            background: linear-gradient(135deg, #E8F7F8 0%, #F0FAFB 100%);
            border: .02rem solid #05C2CB;
          }

          .compare-label {
            display: block;
            font-size: .24rem;
            color: #999;
            margin-bottom: .12rem;
          }

          .compare-value {
            display: block;
            font-size: .32rem;
            font-weight: 700;
            color: #000;
            margin-bottom: .08rem;
          }

          .compare-tip {
            display: block;
            font-size: .22rem;
            color: #666;
          }
        }

        .compare-divider {
          font-size: .28rem;
          color: #999;
          font-weight: 600;
        }
      }
    }
  }

  /* 底部固定购买栏 */
  .bottom-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    box-shadow: 0 -.04rem .24rem rgba(0, 0, 0, 0.1);
    padding: .24rem .36rem;
    padding-bottom: calc(.24rem + env(safe-area-inset-bottom));
    z-index: 100;

    .bar-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: .32rem;

      .price-info {
        flex: 1;

        .price-row {
          display: flex;
          align-items: baseline;
          gap: .16rem;

          .price-label {
            font-size: .26rem;
            color: #666;
          }

          .price-value {
            display: flex;
            align-items: baseline;
            gap: .12rem;

            .original-price {
              font-size: .26rem;
              color: #999;
              text-decoration: line-through;
            }

            .current-price {
              font-size: .48rem;
              font-weight: 700;
              color: #FF6B6B;
            }

            .price-unit {
              font-size: .28rem;
              color: #666;
            }
          }
        }
      }

      .action-buttons {
        .btn-purchase {
          background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
          color: #fff;
          font-size: .32rem;
          font-weight: 700;
          padding: .28rem .64rem;
          border-radius: .48rem;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          box-shadow: 0 .08rem .24rem rgba(5, 194, 203, 0.3);
          transition: all 0.3s ease;

          &:active {
            transform: scale(0.98);
            box-shadow: 0 .04rem .16rem rgba(5, 194, 203, 0.3);
          }
        }
      }
    }
  }
}
</style>
